import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { iaApi, userApi } from '../services/api';

function computeAgeYears(birthDateStr) {
  if (!birthDateStr) return null;
  const d = new Date(birthDateStr);
  if (Number.isNaN(d.getTime())) return null;
  const diff = Date.now() - d.getTime();
  return Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
}

const ProfilePage = () => {
  const { user, updateUser, refreshUser, loading: authLoading } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    level: '',
    goal: '',
    physicalState: '',
    trainingInterests: '',
    previousTrainingBase: '',
    heightCm: '',
    weightKg: '',
    birthDate: '',
  });
  const [selectedEngineId, setSelectedEngineId] = useState('');
  const [engines, setEngines] = useState([]);
  const [aiKeys, setAiKeys] = useState([]);
  const [newAiKey, setNewAiKey] = useState('');
  const [newAiProvider, setNewAiProvider] = useState('mistral');
  const [aiKeysMessage, setAiKeysMessage] = useState({ type: '', text: '' });
  const [editingAiKeyId, setEditingAiKeyId] = useState('');
  const [editingAiKeyValue, setEditingAiKeyValue] = useState('');
  const [aiKeysLoading, setAiKeysLoading] = useState(false);
  const [savingDefaultEngine, setSavingDefaultEngine] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPreviousTrainingBase, setShowPreviousTrainingBase] = useState(false);
  const [trainingBaseSaving, setTrainingBaseSaving] = useState(false);
  const [trainingBaseMessage, setTrainingBaseMessage] = useState({ type: '', text: '' });
  const [aiTestLoading, setAiTestLoading] = useState(false);
  const [aiTestResult, setAiTestResult] = useState('');

  useEffect(() => {
    if (user) {
      const bd =
        user.birthDate != null
          ? String(user.birthDate).slice(0, 10)
          : '';
      setFormData({
        name: user.name || '',
        level: user.level || 'intermediario',
        goal: user.goal || 'hipertrofia',
        physicalState: user.physicalState || '',
        trainingInterests: user.trainingInterests || '',
        previousTrainingBase: user.previousTrainingBase || '',
        heightCm: user.heightCm != null ? String(user.heightCm) : '',
        weightKg: user.weightKg != null ? String(user.weightKg) : '',
        birthDate: bd,
      });
      setSelectedEngineId(user.defaultAiEngineId != null ? String(user.defaultAiEngineId) : '');
    }

    const fetchAiData = async () => {
      try {
        if (!user?.id) return;
        const [enginesRes, keysRes] = await Promise.all([
          iaApi.getEngines(),
          userApi.listAiKeys(user.id),
        ]);
        setEngines(enginesRes.data || []);
        setAiKeys(keysRes.data || []);
      } catch (err) {
        console.error('Error fetching IA data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAiData();
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    try {
      const payload = {
        ...formData,
        heightCm: formData.heightCm === '' ? null : parseInt(formData.heightCm, 10),
        weightKg: formData.weightKg === '' ? null : parseFloat(formData.weightKg),
        birthDate: formData.birthDate || null,
      };
      // O histórico longo tem salvamento dedicado para isolar erros de infra.
      delete payload.previousTrainingBase;
      await updateUser(payload);
      setMessage({ type: 'success', text: 'Perfil atualizado com sucesso!' });
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        'Erro ao atualizar perfil';
      setMessage({ type: 'error', text: msg });
    }
  };

  const handleSaveTrainingBase = async () => {
    setTrainingBaseMessage({ type: '', text: '' });
    setTrainingBaseSaving(true);
    try {
      await updateUser({ previousTrainingBase: formData.previousTrainingBase || '' });
      setTrainingBaseMessage({ type: 'success', text: 'Histórico de treino salvo com sucesso.' });
    } catch (err) {
      const rawMsg =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message ||
        '';
      const normalized = String(rawMsg).toLowerCase();
      const isInfraError =
        normalized.includes('internal error') ||
        normalized.includes('attribute_limit_exceeded') ||
        normalized.includes('attribute') ||
        normalized.includes('appwrite');
      setTrainingBaseMessage({
        type: 'error',
        text: isInfraError
          ? 'Não foi possível gravar o histórico agora (limite de infraestrutura). Tente novamente em instantes.'
          : rawMsg || 'Erro ao salvar histórico de treino.',
      });
    } finally {
      setTrainingBaseSaving(false);
    }
  };

  const getEngineValue = (engine) => String(engine?.id ?? engine?.engineKey ?? engine?.name ?? '');

  const reloadAiKeys = async () => {
    if (!user?.id) return;
    const res = await userApi.listAiKeys(user.id);
    setAiKeys(res.data || []);
  };

  const handleCreateAiKey = async (e) => {
    e.preventDefault();
    setAiKeysMessage({ type: '', text: '' });

    if (!newAiKey.trim()) {
      setAiKeysMessage({ type: 'error', text: 'Informe a API key para cadastrar.' });
      return;
    }

    setAiKeysLoading(true);
    try {
      await userApi.createAiKey(user.id, { provider: newAiProvider, apiKey: newAiKey.trim() });
      await reloadAiKeys();
      setNewAiKey('');
      setAiKeysMessage({ type: 'success', text: 'Chave cadastrada com sucesso.' });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Erro ao cadastrar chave.';
      setAiKeysMessage({ type: 'error', text: msg });
    } finally {
      setAiKeysLoading(false);
    }
  };

  const handleStartEditAiKey = (key) => {
    setEditingAiKeyId(String(key.id));
    setEditingAiKeyValue('');
    setAiKeysMessage({ type: '', text: '' });
  };

  const handleUpdateAiKey = async () => {
    if (!editingAiKeyId) return;
    if (!editingAiKeyValue.trim()) {
      setAiKeysMessage({ type: 'error', text: 'Informe a nova API key para atualizar.' });
      return;
    }

    setAiKeysLoading(true);
    try {
      await userApi.updateAiKey(user.id, editingAiKeyId, { apiKey: editingAiKeyValue.trim() });
      await reloadAiKeys();
      setEditingAiKeyId('');
      setEditingAiKeyValue('');
      setAiKeysMessage({ type: 'success', text: 'Chave atualizada com sucesso.' });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Erro ao atualizar chave.';
      setAiKeysMessage({ type: 'error', text: msg });
    } finally {
      setAiKeysLoading(false);
    }
  };

  const handleDeleteAiKey = async (keyId) => {
    setAiKeysMessage({ type: '', text: '' });
    setAiKeysLoading(true);
    try {
      await userApi.deleteAiKey(user.id, keyId);
      await reloadAiKeys();
      if (String(editingAiKeyId) === String(keyId)) {
        setEditingAiKeyId('');
        setEditingAiKeyValue('');
      }
      setAiKeysMessage({ type: 'success', text: 'Chave removida com sucesso.' });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Erro ao remover chave.';
      setAiKeysMessage({ type: 'error', text: msg });
    } finally {
      setAiKeysLoading(false);
    }
  };

  const handleSaveDefaultEngine = async () => {
    setMessage({ type: '', text: '' });
    if (!selectedEngineId) {
      setMessage({ type: 'error', text: 'Selecione um modelo de IA.' });
      return;
    }
    setSavingDefaultEngine(true);
    try {
      await updateUser({ defaultAiEngineId: selectedEngineId });
      await refreshUser();
      setMessage({ type: 'success', text: 'Motor padrão atualizado com sucesso!' });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Erro ao salvar motor padrão.';
      setMessage({ type: 'error', text: msg });
    } finally {
      setSavingDefaultEngine(false);
    }
  };

  const handleTestAiEngine = async () => {
    setMessage({ type: '', text: '' });
    setAiTestResult('');
    setAiTestLoading(true);
    try {
      const payload = {};
      const res = await iaApi.preSession(payload);
      const data = res?.data || {};
      const orientation = String(data.orientation || '').trim();
      const firstLine = orientation.split('\n')[0] || '';
      setAiTestResult(
        [
          `Engine: ${data.engine || 'desconhecido'}`,
          `Fonte: ${data.source || 'desconhecida'}`,
          `Primeira linha: ${firstLine || '(vazia)'}`,
        ].join('\n')
      );
      setMessage({ type: 'success', text: 'Teste de IA concluído com sucesso.' });
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message ||
        'Falha ao testar o motor de IA.';
      setMessage({ type: 'error', text: msg });
    } finally {
      setAiTestLoading(false);
    }
  };

  const levels = [
    { value: 'iniciante', label: 'Iniciante' },
    { value: 'intermediario', label: 'Intermediário' },
    { value: 'avancado', label: 'Avançado' },
  ];

  const goals = [
    { value: 'hipertrofia', label: 'Hipertrofia' },
    { value: 'forca', label: 'Força' },
    { value: 'resistencia', label: 'Resistência' },
  ];

  const selectedEngine = engines.find((e) => getEngineValue(e) === String(selectedEngineId));

  const bmiDisplay = useMemo(() => {
    const h = parseFloat(formData.heightCm);
    const w = parseFloat(formData.weightKg);
    if (!h || !w || h <= 0) return null;
    const m = h / 100;
    const bmi = w / (m * m);
    if (!Number.isFinite(bmi)) return null;
    return bmi.toFixed(1);
  }, [formData.heightCm, formData.weightKg]);

  const ageYears = useMemo(() => computeAgeYears(formData.birthDate), [formData.birthDate]);

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="font-display text-4xl tracking-wider text-iron-accent mb-2">Perfil</h1>
        <p className="font-mono text-xs uppercase tracking-widest text-iron-muted">
          Dados físicos · objetivos · IA
        </p>
      </div>

      {message.text && (
        <div
          className={`p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-500/10 border border-green-500 text-green-400'
              : 'bg-red-500/10 border border-red-500 text-red-400'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Form */}
        <div className="card">
          <h2 className="text-xl font-semibold text-white mb-6">
            Informações do Perfil
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nome
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nível
              </label>
              <div className="grid grid-cols-3 gap-2">
                {levels.map((l) => (
                  <button
                    key={l.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, level: l.value })}
                    className={`rounded-lg border px-3 py-2 font-mono text-xs transition-colors ${
                      formData.level === l.value
                        ? 'border-iron-accent bg-iron-accent text-black font-medium'
                        : 'border-iron-border text-iron-muted hover:border-iron-accent hover:text-iron-accent'
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Objetivo
              </label>
              <div className="grid grid-cols-3 gap-2">
                {goals.map((g) => (
                  <button
                    key={g.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, goal: g.value })}
                    className={`rounded-lg border px-3 py-2 font-mono text-xs transition-colors ${
                      formData.goal === g.value
                        ? 'border-iron-accent bg-iron-accent text-black font-medium'
                        : 'border-iron-border text-iron-muted hover:border-iron-accent hover:text-iron-accent'
                    }`}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="section-label mb-2 block">Altura (cm)</label>
                <input
                  type="number"
                  min="100"
                  max="250"
                  value={formData.heightCm}
                  onChange={(e) => setFormData({ ...formData, heightCm: e.target.value })}
                  className="input"
                  placeholder="175"
                />
              </div>
              <div>
                <label className="section-label mb-2 block">Peso (kg)</label>
                <input
                  type="number"
                  min="30"
                  max="300"
                  step="0.1"
                  value={formData.weightKg}
                  onChange={(e) => setFormData({ ...formData, weightKg: e.target.value })}
                  className="input"
                  placeholder="75"
                />
              </div>
              <div>
                <label className="section-label mb-2 block">Data nascimento</label>
                <input
                  type="date"
                  value={formData.birthDate}
                  onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                  className="input"
                />
              </div>
            </div>

            {(bmiDisplay || ageYears != null) && (
              <div className="rounded-xl border border-iron-border bg-iron-surface px-4 py-3 font-mono text-xs text-iron-muted">
                {bmiDisplay != null && (
                  <span className="mr-4">
                    IMC: <strong className="text-iron-accent">{bmiDisplay}</strong>
                  </span>
                )}
                {ageYears != null && (
                  <span>
                    Idade: <strong className="text-iron-text">{ageYears}</strong> anos
                  </span>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-iron-muted mb-2 font-mono text-xs uppercase tracking-wide">
                Estado físico atual
              </label>
              <textarea
                value={formData.physicalState}
                onChange={(e) => setFormData({ ...formData, physicalState: e.target.value })}
                className="input min-h-[120px] resize-y"
                placeholder="Lesões, fadiga, sono, limitações articulares..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-iron-muted mb-2 font-mono text-xs uppercase tracking-wide">
                Interesses e preferências de treino
              </label>
              <textarea
                value={formData.trainingInterests}
                onChange={(e) => setFormData({ ...formData, trainingInterests: e.target.value })}
                className="input min-h-[120px] resize-y"
                placeholder="Divisões preferidas, exercícios que gosta ou evita, tempo disponível..."
              />
            </div>

            <div className="rounded-xl border border-iron-border bg-iron-surface/40">
              <button
                type="button"
                onClick={() => setShowPreviousTrainingBase((v) => !v)}
                className="flex w-full items-center justify-between px-4 py-3 text-left"
              >
                <span className="font-mono text-xs uppercase tracking-wider text-iron-muted">
                  Base de treinos que vinha fazendo
                </span>
                <span className="font-mono text-xs text-iron-accent">
                  {showPreviousTrainingBase ? 'Recolher' : 'Expandir'}
                </span>
              </button>
              {showPreviousTrainingBase && (
                <div className="border-t border-iron-border px-4 py-3">
                  <textarea
                    value={formData.previousTrainingBase}
                    onChange={(e) =>
                      setFormData({ ...formData, previousTrainingBase: e.target.value })
                    }
                    className="input min-h-[180px] resize-y"
                    placeholder="Descreve os treinos/split/exercícios que vinha fazendo nas últimas semanas..."
                    maxLength={16000}
                  />
                  <p className="mt-2 text-right font-mono text-[10px] text-iron-muted">
                    {formData.previousTrainingBase.length}/16000
                  </p>
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <button
                      type="button"
                      onClick={handleSaveTrainingBase}
                      disabled={trainingBaseSaving}
                      className="btn-secondary"
                    >
                      {trainingBaseSaving ? 'Salvando histórico...' : 'Salvar histórico de treino'}
                    </button>
                    {trainingBaseMessage.text && (
                      <p
                        className={`text-xs font-mono ${
                          trainingBaseMessage.type === 'success' ? 'text-green-400' : 'text-red-400'
                        }`}
                      >
                        {trainingBaseMessage.text}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <button type="submit" className="btn btn-primary">
              Salvar Perfil
            </button>
          </form>
        </div>

        {/* API Key Form */}
        <div className="card">
          <h2 className="text-xl font-semibold text-white mb-6">
            Configuração de IA
          </h2>

          <div className="space-y-4">
            <div>
              <label className="section-label mb-2 block">
                Modelo de IA
              </label>
              <div className="space-y-2">
                {engines.map((engine) => (
                  <button
                    key={getEngineValue(engine)}
                    type="button"
                    onClick={() => setSelectedEngineId(getEngineValue(engine))}
                    className={`w-full rounded-lg border px-3 py-2 text-left transition-colors ${
                      String(selectedEngineId) === getEngineValue(engine)
                        ? 'border-iron-accent bg-iron-accent/15'
                        : 'border-iron-border hover:border-iron-accent/60'
                    }`}
                  >
                    <div className="font-medium text-iron-text">{engine.name}</div>
                    {engine.description ? (
                      <div className="mt-1 text-xs text-iron-muted">{engine.description}</div>
                    ) : null}
                  </button>
                ))}
              </div>
              {selectedEngine ? (
                <p className="text-xs text-iron-muted mt-2 font-mono">
                  Modelo selecionado: <span className="text-iron-accent">{selectedEngine.name}</span>
                </p>
              ) : null}
            </div>

            <button
              type="button"
              onClick={handleSaveDefaultEngine}
              disabled={savingDefaultEngine}
              className="btn btn-primary"
            >
              {savingDefaultEngine ? 'Salvando motor padrão...' : 'Salvar motor padrão'}
            </button>

            <div className="rounded-xl border border-iron-border bg-iron-surface/40 p-4 space-y-3">
              <h3 className="font-mono text-xs uppercase tracking-wider text-iron-muted">
                Chaves de IA (CRUD)
              </h3>

              <form onSubmit={handleCreateAiKey} className="space-y-3">
                <div>
                  <label className="section-label mb-2 block">Plataforma da chave</label>
                  <div className="grid grid-cols-2 gap-2">
                    {['mistral', 'groq'].map((provider) => (
                      <button
                        key={provider}
                        type="button"
                        onClick={() => setNewAiProvider(provider)}
                        className={`rounded-lg border px-3 py-2 font-mono text-xs transition-colors ${
                          newAiProvider === provider
                            ? 'border-iron-accent bg-iron-accent text-black font-medium'
                            : 'border-iron-border text-iron-muted hover:border-iron-accent hover:text-iron-accent'
                        }`}
                      >
                        {provider.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="section-label mb-2 block">Nova API key</label>
                  <input
                    type="password"
                    value={newAiKey}
                    onChange={(e) => setNewAiKey(e.target.value)}
                    className="input"
                    placeholder="Cole a chave da plataforma selecionada"
                    autoComplete="off"
                  />
                </div>

                <button type="submit" disabled={aiKeysLoading} className="btn-secondary w-full">
                  {aiKeysLoading ? 'Salvando chave...' : 'Adicionar chave'}
                </button>
              </form>

              {aiKeysMessage.text && (
                <p className={`text-xs font-mono ${aiKeysMessage.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                  {aiKeysMessage.text}
                </p>
              )}

              <div className="space-y-2">
                {(aiKeys || []).length === 0 ? (
                  <p className="text-xs font-mono text-iron-muted">Nenhuma chave cadastrada.</p>
                ) : (
                  aiKeys.map((key) => (
                    <div key={key.id} className="rounded-lg border border-iron-border p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="font-mono text-xs text-iron-text">
                          {String(key.provider || '').toUpperCase()} - {key.apiKeyMasked || '***'}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteAiKey(key.id)}
                          disabled={aiKeysLoading}
                          className="rounded-md border border-iron-danger/60 px-2 py-1 text-[10px] font-mono text-iron-danger hover:bg-iron-danger/10"
                        >
                          Excluir
                        </button>
                      </div>

                      {editingAiKeyId === String(key.id) ? (
                        <div className="space-y-2">
                          <input
                            type="password"
                            value={editingAiKeyValue}
                            onChange={(e) => setEditingAiKeyValue(e.target.value)}
                            className="input"
                            placeholder="Nova API key"
                          />
                          <div className="flex gap-2">
                            <button type="button" onClick={handleUpdateAiKey} disabled={aiKeysLoading} className="btn-secondary">
                              Atualizar chave
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingAiKeyId('');
                                setEditingAiKeyValue('');
                              }}
                              className="rounded-lg border border-iron-border px-3 py-2 font-mono text-xs text-iron-muted"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleStartEditAiKey(key)}
                          className="rounded-lg border border-iron-border px-3 py-2 font-mono text-xs text-iron-muted hover:border-iron-accent hover:text-iron-accent"
                        >
                          Editar chave
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={handleTestAiEngine}
              disabled={aiTestLoading}
              className="btn-secondary w-full"
            >
              {aiTestLoading ? 'Testando motor de IA...' : 'Testar feedback do motor de IA'}
            </button>
            {aiTestResult && (
              <pre className="rounded-lg border border-iron-info/40 bg-iron-info/10 p-3 text-xs font-mono whitespace-pre-wrap text-iron-info">
                {aiTestResult}
              </pre>
            )}
          </div>

          <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500 rounded-lg">
            <p className="text-blue-400 text-sm">
              💡 <strong>Dica:</strong> Você pode obter API keys gratuitas em:
            </p>
            <ul className="text-sm text-gray-400 mt-2 space-y-1">
              <li>• Groq: https://console.groq.com</li>
              <li>• Mistral: https://mistral.ai</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Account Info */}
      <div className="card">
        <h2 className="text-xl font-semibold text-white mb-6">
          Informações da Conta
        </h2>
        <div className="space-y-4">
          <div className="flex justify-between py-3 border-b border-gray-700">
            <span className="text-gray-400">Email</span>
            <span className="text-white">{user?.email}</span>
          </div>
          <div className="flex justify-between py-3 border-b border-gray-700">
            <span className="text-gray-400">Conta criada em</span>
            <span className="text-white">
              {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('pt-BR') : 'N/A'}
            </span>
          </div>
          <div className="flex justify-between py-3">
            <span className="text-gray-400">Status</span>
            <span className="text-green-400">Ativa</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
